import React, { useState, useEffect } from "react";
import { Slider } from "src/components/shadcn/Slider";
import { Input } from "src/components/shadcn/Input";

interface AmountSelectorProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  /** Called when the slider max needs to expand (e.g. typed input exceeds current max) */
  onMaxChange?: (newMax: number) => void;
  /** Hard ceiling that the max cannot exceed */
  hardMax?: number;
}

const AmountSelector: React.FC<AmountSelectorProps> = ({
  value,
  onChange,
  min,
  max,
  step,
  onMaxChange,
  hardMax,
}) => {
  const effectiveMax = hardMax ? Math.min(max, hardMax) : max;

  // Track input string separately to allow empty field while typing
  const [inputValue, setInputValue] = useState(value.toLocaleString());

  // Sync input when value changes externally (e.g., from slider)
  useEffect(() => {
    setInputValue(value.toLocaleString());
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/,/g, "");
    setInputValue(rawValue);

    const val = parseInt(rawValue, 10);
    if (!isNaN(val)) {
      onChange(val);
      // Expand slider max if needed
      if (onMaxChange) {
        const needed = Math.ceil(val / step) * step;
        const ceiling = hardMax ?? Infinity;
        if (needed > max) {
          onMaxChange(Math.min(needed, ceiling));
        }
      }
    }
  };

  const handleBlur = () => {
    const val = parseInt(inputValue.replace(/,/g, ""), 10);
    const ceiling = hardMax ?? effectiveMax;
    const finalValue = isNaN(val) ? min : Math.min(Math.max(Math.round(val / step) * step, min), ceiling);
    onChange(finalValue);
    setInputValue(finalValue.toLocaleString());
  };

  return (
    <div className="flex items-start gap-4">
      <div className="flex-1 px-2 pt-4">
        <Slider
          value={[value]}
          onValueChange={(v) => onChange(v[0])}
          min={min}
          max={effectiveMax}
          step={step}
        />
        <div className="flex justify-between text-sm text-gray-500 mt-1">
          <span>${min.toLocaleString()}</span>
          <span>${effectiveMax.toLocaleString()}</span>
        </div>
      </div>
      <div className="relative flex items-center">
        <span className="absolute left-3 text-gray-500 pointer-events-none">$</span>
        <Input
          type="text"
          inputMode="numeric"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          className="w-28 pl-7 pr-2 text-center font-medium"
        />
      </div>
    </div>
  );
};

export default AmountSelector;
